import { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Copy, Check, Download, Printer } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@foundation/altzero_base/base/components/ui/dialog';
import { Button } from '@foundation/altzero_base/base/components/ui/button';
import { QRCodePreview, buildQRUrl } from './QRCodePreview';
import { generateA4Poster, generateSticker, generateDigitalPng } from '../services/qrTemplateService';
import type { Venue } from '../types/venue.types';
import { theme } from '../../../theme';

type QRCodeDialogProps = {
  open: boolean;
  onClose: () => void;
  venue: Venue;
};

export const QRCodeDialog = ({ open, onClose, venue }: QRCodeDialogProps) => {
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const qrUrl = buildQRUrl(venue.venue_slug);

  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText(qrUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getQRCanvasDataUrl = (): string | null => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return null;
    return canvas.toDataURL('image/png');
  };

  const handleDownload = async (format: 'poster' | 'sticker' | 'png') => {
    setGenerating(format);
    try {
      const qrDataUrl = getQRCanvasDataUrl();
      if (!qrDataUrl) return;

      if (format === 'poster') {
        await generateA4Poster(qrDataUrl, venue.venue_name, qrUrl);
      } else if (format === 'sticker') {
        await generateSticker(qrDataUrl, venue.venue_name, qrUrl);
      } else {
        await generateDigitalPng(qrDataUrl, venue.venue_name);
      }
    } catch (error) {
      console.error('Failed to generate template:', error);
    } finally {
      setGenerating(null);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>QR Code: {venue.venue_name}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center py-4">
          {/* QR Preview (SVG for display) */}
          <QRCodePreview
            venueSlug={venue.venue_slug}
            size={220}
            includeLabel
            venueName={venue.venue_name}
          />

          {/* Hidden canvas for export */}
          <div ref={canvasRef} className="hidden">
            <QRCodeCanvas
              value={qrUrl}
              size={600}
              level="M"
              includeMargin
              bgColor="#ffffff"
              fgColor="#0a1929"
            />
          </div>

          {/* URL display */}
          <p className={`mt-4 text-xs ${theme.text.body} font-mono text-center break-all max-w-full px-4`}>
            {qrUrl}
          </p>

          {/* Copy URL */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyUrl}
            className="mt-3"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1" />
                Copy URL
              </>
            )}
          </Button>
        </div>

        {/* Download options */}
        <div className="space-y-2">
          <p className={`text-sm font-medium ${theme.text.body}`}>Download as:</p>
          <div className="grid grid-cols-1 gap-2">
            <Button
              variant="outline"
              onClick={() => handleDownload('poster')}
              disabled={!!generating}
              className="justify-start"
            >
              <Download className="w-4 h-4 mr-2" />
              {generating === 'poster' ? 'Generating...' : 'A4 Poster PDF'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDownload('sticker')}
              disabled={!!generating}
              className="justify-start"
            >
              <Download className="w-4 h-4 mr-2" />
              {generating === 'sticker' ? 'Generating...' : 'Sticker PDF'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDownload('png')}
              disabled={!!generating}
              className="justify-start"
            >
              <Download className="w-4 h-4 mr-2" />
              {generating === 'png' ? 'Generating...' : 'Digital PNG'}
            </Button>
          </div>
        </div>

        <Button variant="ghost" onClick={handlePrint} className="mt-2">
          <Printer className="w-4 h-4 mr-2" />
          Print
        </Button>
      </DialogContent>
    </Dialog>
  );
};
